import { memo, useCallback } from 'react';
import { Button } from '@monksflow/monks-ui';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import type { CarouselControlsProps, DotIndicatorProps } from './CarouselControls.types';
import { useTranslation } from 'react-i18next';

/**
 * DotIndicator - Individual dot indicator for carousel navigation
 * Memoized to prevent re-renders when other dots change
 */
const DotIndicator = memo(function DotIndicator({
  index,
  isActive,
  onDotClick,
}: DotIndicatorProps) {
  const { t } = useTranslation('contentTest');

  const handleClick = useCallback(() => {
    onDotClick(index);
  }, [onDotClick, index]);

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={t('ideationWidget.carouselControls.goToSlide', { slide: index + 1 })}
      aria-current={isActive ? 'true' : undefined}
      // The active dot widens into a pill; only width, border-radius and colour
      // change, so the transition names exactly those.
      className={`h-2.5 cursor-pointer rounded-full border-none p-0 transition-[width,border-radius,background-color] duration-base ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
        isActive ? 'w-8 bg-foreground' : 'w-2.5 bg-border-strong hover:bg-muted-foreground'
      }`}
    />
  );
});

/** Glass-morphism puck the chevron buttons sit inside. */
const PUCK = 'flex items-center justify-center rounded-full border border-border bg-card/90 p-0.5';

/**
 * CarouselControls - Navigation controls for the benchmark carousel
 *
 * Features:
 * - Previous/Next buttons with glass morphism style
 * - Dot indicators for direct slide navigation
 * - Memoized to prevent unnecessary re-renders
 * - Accessible with proper ARIA labels
 */
export const CarouselControls = memo(function CarouselControls({
  currentIndex,
  totalCount,
  onPrevious,
  onNext,
  onDotClick,
}: CarouselControlsProps) {
  const { t } = useTranslation('contentTest');

  return (
    <div className="flex items-center justify-center gap-8">
      <div className={PUCK}>
        <Button
          variant="ghost"
          size="icon-default"
          roundness="full"
          aria-label={t('ideationWidget.carouselControls.previousSlide')}
          onClick={onPrevious}
        >
          <ChevronLeftIcon className="size-6" />
        </Button>
      </div>

      {/* Dot Indicators */}
      <div className="flex items-center gap-3">
        {Array.from({ length: totalCount }).map((_, index) => (
          <DotIndicator
            key={index}
            index={index}
            isActive={index === currentIndex}
            onDotClick={onDotClick}
          />
        ))}
      </div>

      <div className={PUCK}>
        <Button
          variant="ghost"
          size="icon-default"
          roundness="full"
          aria-label={t('ideationWidget.carouselControls.nextSlide')}
          onClick={onNext}
        >
          <ChevronRightIcon className="size-6" />
        </Button>
      </div>
    </div>
  );
});
